
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { useAuthActions } from "@contexts/auth";
import { cloneElement, useEffect, useState } from "react";
import { BRAZILIAN_STATES } from "@utils/brazilianStates";
import InfoTooltip from "@components/InfoTooltip";
import BuildingAccessPanel from "./BuildingAccessPanel";

const REQUIRED_FIELDS = ['company_id', 'building_code', 'company_code', 'building_name'];

// Índice usado para corrigir o orçamento. CUB é publicado por Sinduscon estadual — por isso
// exige UF; INCC é nacional, não varia por estado.
const INDEXER_FAMILIES = ['CUB', 'INCC'];

// Categorias padronizadas de projeto-padrão do CUB (NBR 12721 / Sinduscon).
const CUB_CATEGORIES = ['Residencial', 'Comercial', 'Industrial', 'Galpão Industrial'];

// Sienge classifica movimentos bancários por um id numérico; esses 4 tipos (transferência
// entre contas, IOF, etc.) são rotineiramente excluídos da reconciliação financeira e por
// isso valem como default editável — mas só faz sentido para construtoras Sienge.
const DEFAULT_SIENGE_BANK_MOVEMENT_IDS = "14,16,21,22";

// Toda obra cadastrada pelo Backoffice é MC (monitoramento e controle) — o campo Scope deixou
// de ser exibido no formulário, mas o valor continua sendo enviado ao back-end.
const FIXED_SCOPE = 'mc';

// Rótulos em português — o VALOR salvo no banco continua em inglês (contrato já usado pelo
// resto do sistema); só o texto exibido ao operador é traduzido.
const FIELD_LABELS = {
  building_code: 'Código da Obra',
  company_code: 'Código da Construtora',
  building_name: 'Nome da Obra',
  city: 'Cidade',
  state: 'UF',
  start_date: 'Data de Início',
  end_date: 'Data de Término',
  area: 'Área',
  budget_version: 'Versão do Orçamento',
  building_constructive_units_id: 'Unidades Construtivas',
  cost_centers_id: 'Centros de Custo',
  building_cost_centers_id: 'Centros de Custo da Obra',
  appropriation_level: 'Nível de Apropriação',
  plan_tool: 'Ferramenta de Planejamento',
  bank_movement_id_to_remove: 'IDs de Movimento Bancário a Excluir',
  cost_centers_id_comercial: 'Centros de Custo Comercial',
  bill_id_to_remove: 'IDs de Título a Excluir',
};

// Disclaimer comum aos campos que aceitam uma lista de IDs: em branco = traz tudo; preenchido
// = traz só o que foi informado; múltiplos valores sempre separados por vírgula, sem espaço.
const idListHint = (whatIfBlank) => (
  `Se deixar em branco, traz ${whatIfBlank}. Se preencher, traz apenas o(s) informado(s). `
  + `Para mais de um valor, separe por vírgula, sem espaço (ex.: 12,34,56).`
);

const ID_LIST_HINTS = {
  building_constructive_units_id: idListHint('todas as unidades construtivas'),
  cost_centers_id: idListHint('todos os centros de custo'),
  building_cost_centers_id: idListHint('todos os centros de custo da obra'),
  cost_centers_id_comercial: idListHint('todos os centros de custo comerciais'),
  bill_id_to_remove: 'IDs de título (bill) a excluir da apuração financeira. Em branco = não '
    + 'exclui nenhum. Para mais de um valor, separe por vírgula, sem espaço (ex.: 12,34,56).',
};

const APPROPRIATION_LEVEL_OPTIONS = [
  { value: 'unit', label: 'Unidade' },
  { value: 'constructivecell', label: 'Célula Construtiva' },
  { value: 'step', label: 'Etapa' },
  { value: 'substep', label: 'Subetapa' },
  { value: 'service', label: 'Serviço' },
];

// "prevision"/"project" são nomes de ferramenta (Prevision, AMPLI Project), não palavras
// genéricas — mantidos como estão em vez de traduzidos.
const PLAN_TOOL_OPTIONS = ['prevision', 'project'];

// Cada campo vira uma célula própria do grid — sem isso o fieldset (grid, pro mesmo padrão
// visual do restante do formulário) intercalava label/input em colunas separadas em vez de
// empilhar label-sobre-input dentro da mesma célula.
const Field = ({ label, full, hint, children }) => {
  const fieldId = children.props.id || children.props.name
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <label htmlFor={fieldId}>{label}{hint && <InfoTooltip text={hint} />}</label>
      {cloneElement(children, { id: fieldId })}
    </div>
  )
}

// O data-pipeline lê estes campos de `buildings` sem checar ausência (`cost_centers_id_comercial`
// é indexado direto em `erp_sienge/extract/extractIncome.py`). Vazio é um valor válido — o que
// quebra a ingestão é o campo NÃO EXISTIR no documento, então o formulário sempre os envia.
const emptyBuilding = {
  city: '',
  state: '',
  cost_centers_id_comercial: '',
  building_constructive_units_id: '',
  bill_id_to_remove: '',
  stock: false,
}

// `bill_id_to_remove` está persistido como ARRAY (ex.: [12]) — o input é texto separado por
// vírgula, mesmo padrão dos outros campos de lista de ids desta tela.
const idListToText = (value) => {
  // `[null]` é o "vazio" convencionado no banco (ver o comentário no envio) — na tela tem que
  // aparecer como campo em branco, não como a string "null".
  if (Array.isArray(value)) return value.filter(id => id !== null && id !== undefined).join(',')
  return value ?? ''
}

const buildingToFormState = (building) => ({
  ...building,
  company_id: `${building.company_id}`,
  city: building.city ?? '',
  state: building.state ?? '',
  cost_centers_id_comercial: building.cost_centers_id_comercial ?? '',
  bill_id_to_remove: idListToText(building.bill_id_to_remove),
  start_date: building.start_date ? new Date(building.start_date).toISOString().slice(0, 10) : '',
  end_date: building.end_date ? new Date(building.end_date).toISOString().slice(0, 10) : '',
  // Só ano-mês: o campo virou seletor de mês, porque a série do índice é mensal (dia 1º).
  budget_delivery_date: building.budget_delivery_date ? new Date(building.budget_delivery_date).toISOString().slice(0, 7) : '',
  indexer_description: building.indexer?.description || '',
  indexer_type: building.indexer?.type || '',
  indexer_uf: building.indexer?.uf || '',
})

// building: opcional — quando informado, o formulário abre em modo de edição (chama
// updateBuilding em vez de createBuilding). onSuccess: opcional — chamado após
// criação/edição (usado quando o formulário é aberto dentro de um Modal).
const NewBuilding = ({ building, onSuccess }) => {
  const isEditing = !!building
  const [newBuilding, setNewBuilding] = useState(building ? buildingToFormState(building) : emptyBuilding)
  const [companies, setCompanies] = useState([])
  const [deleteConfirming, setDeleteConfirming] = useState(false)
  const [actionFeedback, setActionFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { listCompanies, createBuilding, updateBuilding, deleteBuilding } = useProjectActions();

  const getCompanies = async () => {
    const res = await listCompanies();
    setCompanies([...(res?.companies || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')))
  }

  useEffect(() => {
    getCompanies();
  }, [])

  const selectedCompany = companies.find(c => `${c._id}` === `${newBuilding.company_id}`)

  // ERP da construtora selecionada é a regra primária que direciona os campos específicos da
  // obra — igual à decisão já tomada em newCompany.jsx. Sienge ganha um default editável para
  // bank_movement_id_to_remove; outros ERPs não usam esse campo, então não o pré-preenchemos.
  useEffect(() => {
    if (selectedCompany?.erp === 'SIENGE' && newBuilding.bank_movement_id_to_remove === undefined) {
      setNewBuilding(prev => ({ ...prev, bank_movement_id_to_remove: DEFAULT_SIENGE_BANK_MOVEMENT_IDS }))
    }
  }, [selectedCompany?.erp])

  // Campos que o domínio exige em maiúsculas — ver o comentário sobre a staging zone acima.
  const UPPERCASE_FIELDS = ['company_code']

  const handleInput = (e) => {
    // O código acompanha o subdomain somente quando o operador escolhe/troca a construtora.
    // Carregar a lista durante uma edição não pode sobrescrever um código legado diferente.
    if (e.target.name === 'company_id') {
      const nextCompany = companies.find(c => `${c._id}` === `${e.target.value}`)
      setNewBuilding(prev => ({
        ...prev,
        company_id: e.target.value,
        ...(nextCompany?.subdomain ? { company_code: nextCompany.subdomain.toUpperCase() } : {}),
      }))
      return
    }
    if (e.target.type === 'number') {
      setNewBuilding(prev => ({ ...prev, [e.target.name]: +e.target.value }))
    }
    else {
      const value = UPPERCASE_FIELDS.includes(e.target.name)
        ? e.target.value.toUpperCase()
        : e.target.value
      setNewBuilding(prev => ({ ...prev, [e.target.name]: value }))
    }
  }

  const hasRequiredFields = REQUIRED_FIELDS.every(field => {
    const value = newBuilding[field]
    return value !== undefined && value !== '' && value !== 'choose'
  })

  // O cadastro não é mais bloqueado por teste de conexão: a conectividade com o ERP é atributo
  // da CONSTRUTORA e é testada no formulário dela (`newCompany.jsx`). Aqui o gate seguinte é a
  // primeira execução do pipeline, que roda depois de salvar e valida o cadastro de verdade.
  const sendDisabled = !hasRequiredFields || submitting

  const handleSend = async (e) => {
    e.preventDefault()
    if (sendDisabled) return
    setSubmitting(true)
    setActionFeedback('')
    try {
      const formattedBuilding = {
        ...newBuilding,
        start_date: new Date(newBuilding.start_date),
        end_date: new Date(newBuilding.end_date),
        // `YYYY-MM` -> dia 1º em UTC. Sem o `-01` explícito, `new Date('2026-01')` já daria dia 1º,
        // mas deixar implícito esconderia a invariante de quem ler depois.
        budget_delivery_date: new Date(`${newBuilding.budget_delivery_date}-01T00:00:00.000Z`),
        company_code: (newBuilding.company_code || '').toUpperCase(),
        indexer: {
          description: newBuilding.indexer_description,
          type: newBuilding.indexer_type,
          uf: newBuilding.indexer_uf,
        },
        scope: FIXED_SCOPE,
        // Persistido como array — o input é texto separado por vírgula. ⚠️ Vazio NÃO pode virar
        // `[]`: o pipeline monta o contexto da obra com `pd.DataFrame(buildingData)`, e o pandas
        // alinha todas as colunas ao tamanho da única que é lista — com `[]` o DataFrame vai a
        // zero linhas e a ingestão morre com `KeyError: 0` no `load`. `[null]` é a convenção do
        // dado real (uma linha, nulo = "não excluir nenhum título").
        bill_id_to_remove: (() => {
          const ids = String(newBuilding.bill_id_to_remove ?? '')
            .split(',')
            .map(id => id.trim())
            .filter(Boolean)
            .map(Number)
            .filter(id => Number.isFinite(id))
          return ids.length > 0 ? ids : [null]
        })(),
        // Sempre enviados, mesmo vazios/falsos: o pipeline indexa estes campos sem guarda de
        // ausência (`.loc[0, 'building_constructive_units_id'].split(",")` em
        // `erp_sienge/transform/transformBuildingCostEstimationsItems.py`), e campo AUSENTE não
        // vira coluna no json_normalize → KeyError. Vazio é válido e significa "traz tudo".
        cost_centers_id_comercial: newBuilding.cost_centers_id_comercial ?? '',
        building_constructive_units_id: newBuilding.building_constructive_units_id ?? '',
        stock: newBuilding.stock === true,
      }
      delete formattedBuilding.indexer_description
      delete formattedBuilding.indexer_type
      delete formattedBuilding.indexer_uf
      // bank_movement_id_to_remove só existe para Sienge — se o operador trocar de construtora
      // para uma não-Sienge depois de preenchido, não envia o campo.
      if (selectedCompany?.erp !== 'SIENGE') delete formattedBuilding.bank_movement_id_to_remove

      const result = isEditing
        ? await updateBuilding({ ...formattedBuilding, building_id: building._id })
        : await createBuilding(formattedBuilding)

      if (result?.ok && !isEditing) {
        setNewBuilding(emptyBuilding)
      }
      if (!result?.ok) {
        setActionFeedback(result?.message || 'Não foi possível salvar a obra.')
      } else {
        setActionFeedback('')
      }
      onSuccess?.(result)
    } catch {
      setActionFeedback('Não foi possível salvar a obra.')
    } finally {
      setSubmitting(false)
    }
  }

  // (Des)ativar é reversível — Remover é definitivo (não toca o histórico de medição em
  // `wbs_buildings`, só o cadastro em `buildings`).
  const toggleActive = async () => {
    const active = building.active === false
    const result = await updateBuilding({ ...building, building_id: building._id, active })
    if (result?.ok) onSuccess?.(result)
    else setActionFeedback(result?.message || 'Não foi possível alterar o status.')
  }

  const confirmDelete = async () => {
    const result = await deleteBuilding(building._id)
    if (result?.ok) {
      onSuccess?.(result)
    } else {
      setActionFeedback(result?.message || 'Não foi possível remover.')
      setDeleteConfirming(false)
    }
  }

  const getLabel = (name) => FIELD_LABELS[name] || name.split("_").map(
    (word) => word[0].toUpperCase() + word.substring(1)
  ).join(" ")

  const fields = [{
    name: 'building_code',
    type: 'number',
  }, {
    name: 'company_code',
    type: 'text',
  }, {
    name: 'building_name',
    type: 'text',
  }, {
    name: 'city',
    type: 'text',
  }, {
    name: 'state',
    type: 'select',
    optional: true,
    options: BRAZILIAN_STATES.map(({ uf, name }) => ({ value: uf, label: `${uf} — ${name}` })),
  }, {
    name: 'start_date',
    type: 'date',
  }, {
    name: 'end_date',
    type: 'date',
  }, {
    name: 'area',
    type: 'number',
  }, {
    name: 'budget_version',
    type: 'number',
  }, {
    name: 'building_constructive_units_id',
    type: 'text',
  }, {
    name: 'cost_centers_id',
    type: 'text',
  }, {
    name: 'cost_centers_id_comercial',
    type: 'text',
  }, {
    name: 'building_cost_centers_id',
    type: 'text',
  }, {
    name: 'appropriation_level',
    type: 'select',
    options: APPROPRIATION_LEVEL_OPTIONS,
  }, {
    name: 'plan_tool',
    type: 'select',
    options: PLAN_TOOL_OPTIONS.map(value => ({ value, label: value })),
  },
  ]

  return (
    <>
      <div className="backoffice-form-card">
        <h2>{isEditing ? 'Editar Obra' : 'Nova Obra'}</h2>
        <form className="new-building-container">
          <div>
            <label>Construtora</label>
            <select name="company_id" value={newBuilding.company_id || 'choose'} onChange={handleInput}>
              <option disabled value='choose'>Choose</option>
              {companies.map(i => {
                return <option key={i._id} value={`${i._id}`}>{i.name}</option>
              })}
            </select>
          </div>
          {fields.map(i => {
            if (i.type === 'select') {
              return <Field key={i.name} label={getLabel(i.name)}>
                <select
                  name={i.name}
                  value={newBuilding[i.name] || (i.optional ? '' : 'choose')}
                  onChange={handleInput}
                >
                  <option disabled={!i.optional} value={i.optional ? '' : 'choose'}>
                    {i.optional ? 'Não informado' : 'Choose'}
                  </option>
                  {i.options.map(j => {
                    return <option key={j.value} value={j.value}>{j.label}</option>
                  })}
                </select>
              </Field>
            } else {
              return <Field key={i.name} label={getLabel(i.name)} hint={ID_LIST_HINTS[i.name]}>
                <input type={i.type} name={i.name} onChange={handleInput} value={newBuilding[i.name] ?? ''} />
              </Field>
            }
          })}

          <fieldset className="indexer-fieldset">
            <legend>Índice de correção</legend>
            <Field label="Índice">
              <select name="indexer_description" onChange={handleInput} value={newBuilding.indexer_description || ''}>
                <option disabled value=''>Choose</option>
                {INDEXER_FAMILIES.map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            </Field>

            {newBuilding.indexer_description === 'CUB' && (
              <>
                <Field label="Categoria (CUB)">
                  <select name="indexer_type" onChange={handleInput} value={newBuilding.indexer_type || ''}>
                    <option disabled value=''>Choose</option>
                    {CUB_CATEGORIES.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                </Field>
                <Field label="UF (Sinduscon)">
                  <select name="indexer_uf" onChange={handleInput} value={newBuilding.indexer_uf || ''}>
                    <option disabled value=''>Choose</option>
                    {BRAZILIAN_STATES.map(({ uf, name }) => <option key={uf} value={uf}>{uf} — {name}</option>)}
                  </select>
                </Field>
              </>
            )}

            {newBuilding.indexer_description === 'INCC' && (
              <Field label="Categoria (opcional)">
                <input type="text" name="indexer_type" onChange={handleInput} value={newBuilding.indexer_type || ''} />
              </Field>
            )}

            <Field
              label="Mês de Entrega do Orçamento"
              hint={'O índice de correção (CUB/INCC) usa este mês como referência — por isso os dois '
                + 'campos ficam juntos. A série do índice é mensal, então só o mês é escolhido e a '
                + 'data é gravada sempre no dia 1º.'}
            >
              {/* `type="month"` em vez de `type="date"`: torna IMPOSSÍVEL escolher outro dia. O
                  pipeline busca o índice-base por igualdade exata em `date_used_in`, e a série
                  só tem dia 1º — qualquer outro dia derrubava a execução em milissegundos. */}
              <input type="month" name="budget_delivery_date" onChange={handleInput} value={newBuilding.budget_delivery_date || ''} />
            </Field>
          </fieldset>

          {selectedCompany?.erp === 'SIENGE' && (
            <Field
              label={getLabel('bank_movement_id_to_remove')}
              hint="IDs de tipo de movimento bancário Sienge a excluir da reconciliação. Preenchido com o padrão usado nas demais construtoras Sienge — ajuste se necessário."
            >
              <input
                type="text"
                name="bank_movement_id_to_remove"
                onChange={handleInput}
                value={newBuilding.bank_movement_id_to_remove ?? DEFAULT_SIENGE_BANK_MOVEMENT_IDS}
              />
            </Field>
          )}

          <Field
            label={getLabel('bill_id_to_remove')}
            hint={ID_LIST_HINTS.bill_id_to_remove}
          >
            <input
              type="text"
              name="bill_id_to_remove"
              onChange={handleInput}
              value={newBuilding.bill_id_to_remove ?? ''}
            />
          </Field>

          <div className="checkbox-row" style={{ gridColumn: '1 / -1' }}>
            <label>
              <input
                type="checkbox"
                checked={newBuilding.stock === true}
                onChange={(e) => setNewBuilding(prev => ({ ...prev, stock: e.target.checked }))}
              />
              Obra com controle de estoque
              <InfoTooltip text="Habilita a ingestão e a atualização de estoque para esta obra. Sem isso, a atualização de estoque não fica disponível no dashboard." />
            </label>
          </div>

          {/* A PRIMEIRA execução (e a liberação inicial de acesso) fica no botão "Registrar
              dados" da listagem de obras, não aqui — é o passo que se faz uma vez. Este painel
              é a manutenção depois disso: precisa da obra já persistida E já ingerida, porque
              grava o `_id` de `wbs_buildings` em `users.buildingIds`. */}
          {isEditing && (
            <div style={{ gridColumn: '1 / -1' }}>
              <BuildingAccessPanel buildingId={building._id} />
            </div>
          )}

          {isEditing && building.active === false && (
            <p className="inactive-badge">Esta obra está desativada.</p>
          )}

          {deleteConfirming && (
            <div className="reset-password-confirm">
              <p>Remover definitivamente "{building.building_name || building.description}"? Essa ação não pode ser desfeita.</p>
              <button type="button" className="button button-red" onClick={confirmDelete}>Confirmar remoção</button>
              <button type="button" className="button" onClick={() => setDeleteConfirming(false)}>Cancelar</button>
            </div>
          )}
          {actionFeedback && (
            <p className="reset-password-feedback" role="alert" aria-live="assertive">
              {actionFeedback}
            </p>
          )}

          <div className="edit-user-actions">
            <button
              disabled={sendDisabled}
              className="button button-accent"
              onClick={handleSend}
              aria-busy={submitting}
            >
              {submitting ? 'Salvando...' : (isEditing ? 'Salvar' : 'Enviar')}
            </button>
            {isEditing && (
              <>
                <button type="button" className="button" onClick={toggleActive}>
                  {building.active === false ? 'Ativar' : 'Desativar'}
                </button>
                <button type="button" className="button button-red" onClick={() => setDeleteConfirming(true)}>Remover</button>
              </>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default NewBuilding;
